// i18n: translated
/**
 * 債権者 CSV export フック (steering 304 Slice D1)
 *
 * GET /cases/{caseId}/creditors/export.csv を叩いて CSV (text/csv) を取得し、Blob download する。
 * apiFetch は JSON レスポンス前提のため、CSV は fetch + Blob で直接扱う (useExportCsv / useExportBudgetPdf と同型)。
 *
 * BE 側 (cases.ts exportCreditorsCsv) が data scope / audit / 日本語 header / UTF-8 BOM / deleted 除外を担うため、
 * FE は取得して download するだけ (CSV 生成ロジックを FE に二重定義しない)。
 */
import { useCallback, useState } from 'react';
import { useToast } from '@/hooks/useToast';
import { useT } from '@/i18n';
import { creditorExportsNs } from '@/i18n/namespaces/creditorExports';

/**
 * 債権者 CSV export の request URL を構築する純関数 (Network / Amplify から分離してテスト可能にする)。
 * 例: buildCreditorsExportCsvUrl('/api', 'c 1') → '/api/cases/c%201/creditors/export.csv'
 */
export function buildCreditorsExportCsvUrl(apiBase: string, caseId: string): string {
  return `${apiBase}/cases/${encodeURIComponent(caseId)}/creditors/export.csv`;
}

/**
 * Content-Disposition ヘッダーから filename を取り出す純関数。取得できなければ fallback を返す。
 */
export function parseContentDispositionFilename(
  disposition: string | null,
  fallback: string,
): string {
  if (!disposition) return fallback;
  const match = disposition.match(/filename="?([^";\n]+)"?/);
  return match?.[1] ?? fallback;
}

export function useExportCreditorsCsv() {
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();
  // トーストだけを表示言語で出す (CSV の中身・列名は BE が日本語で作る。外の形式との契約なので訳さない)
  const t = useT(creditorExportsNs);

  const exportCsv = useCallback(
    async (caseId: string): Promise<boolean> => {
      if (!caseId) return false;
      setLoading(true);
      try {
        // apiFetch と同じ認証トークン取得ロジック
        const { fetchAuthSession } = await import('aws-amplify/auth');
        const session = await fetchAuthSession();
        const authToken = session.tokens?.idToken?.toString() ?? '';

        const apiBase = import.meta.env.DEV
          ? '/api'
          : import.meta.env.VITE_API_URL || 'https://api.ace-collect.com';

        const res = await fetch(buildCreditorsExportCsvUrl(apiBase, caseId), {
          headers: {
            ...(authToken && { Authorization: `Bearer ${authToken}` }),
          },
        });

        // 失敗を成功と誤認しない (CLAUDE.md §4.5: response.ok を必ず確認する)
        if (!res.ok) {
          showToast('error', t('csvFailedStatus', { status: res.status }));
          return false;
        }

        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Content-Disposition からファイル名を取得、なければデフォルト (BE の命名規則に揃える)
        const fallbackName = `creditors_${caseId}_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.csv`;
        a.download = parseContentDispositionFilename(
          res.headers.get('Content-Disposition'),
          fallbackName,
        );
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        showToast('success', t('csvDone'));
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : t('csvFailed');
        showToast('error', message);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [showToast, t],
  );

  return { exportCsv, loading };
}
